import { createRequire } from 'node:module';
import { join } from 'node:path';
import { cwd } from 'node:process';
import { pathToFileURL } from 'node:url';
import { CliError, CliErrorCode } from '@wix/cli-error';
import semverPrerelease from 'semver/functions/prerelease.js';
import type { WixConfigSchema } from './files/wix.config.js';
import packageJson from '../package.json' with { type: 'json' };

const requireCwd = createRequire(
  join(
    cwd(),
    // `createRequire` must accept a path to a file, but doesn't really care about it
    // Even Sindre did it:
    // https://github.com/sindresorhus/import-from/blob/6063941cfac93086ae7f5a2e9ec2ac23f4bee401/index.js#L9
    'dummy.js'
  )
);

export async function importCliApp(config: WixConfigSchema) {
  if (Object.hasOwn(config, 'appId')) {
    const cliAppPath = resolveCliAppPath();
    if (cliAppPath) {
      ensureCompatibleVersions();
      return importCliAppFromPath(cliAppPath);
    }
  }

  return null;
}

function resolveCliAppPath() {
  try {
    return requireCwd.resolve('@wix/cli-app');
  } catch (e) {
    if (e instanceof Error && 'code' in e && e.code === 'MODULE_NOT_FOUND') {
      return null;
    }

    throw new CliError({
      cause: e,
      code: CliErrorCode.FailedToImportCliApp(),
    });
  }
}

function ensureCompatibleVersions() {
  const cliAppVersion = requireCwd('@wix/cli-app/package.json').version;

  // Skip version check for prerelease versions (e.g. falcon deploy previews)
  if (semverPrerelease(cliAppVersion) != null) {
    return;
  }

  if (packageJson.version !== cliAppVersion) {
    throw new CliError({
      cause: null,
      code: CliErrorCode.CliAppVersionMismatch({
        cliAppVersion,
        cliVersion: packageJson.version,
      }),
    });
  }
}

async function importCliAppFromPath(cliAppPath: string) {
  try {
    return await import(pathToFileURL(cliAppPath).href);
  } catch (e) {
    throw new CliError({
      cause: e,
      code: CliErrorCode.FailedToImportCliApp(),
    });
  }
}
